/**
 * NIST SP 800-63B-4 (B-3.1.1.2) compliant password validator.
 *
 * Rules:
 *   - Minimum length 8 (no upper bound).
 *   - No composition rules (uppercase / lowercase / digit / special chars).
 *   - Reject context-specific terms: substrings of the user's email local-part,
 *     first name, or last name (case-insensitive, NFC-normalized, ≥ 3 chars only).
 *
 * Common-password and breached-password checks are performed server-side
 * (account-api / IDme::Validation::Password) — errors are surfaced as
 * backend error pills via the existing flow.
 *
 * @param {string|undefined} password
 * @param {{ email?: string|null, fname?: string|null, lname?: string|null }} [userContext]
 * @returns {{ isValid: boolean, errors: Array<{ code: string, params?: object }> }}
 */
export const MIN_LENGTH = 8;
const CONTEXT_TERM_MIN_LENGTH = 3;

// Opaque error codes. Consumers map codes to i18n keys at render time;
// keeping the validator presentation-agnostic per PR #512 review.
export const PASSWORD_ERROR_CODES = Object.freeze({
  MIN_LENGTH: 'MIN_LENGTH',
  CONTAINS_EMAIL: 'CONTAINS_EMAIL',
  CONTAINS_NAME: 'CONTAINS_NAME',
});

// Spread iteration yields Unicode code points, so high-plane characters
// (emoji etc.) count as one each — required by NIST SP 800-63B-4 #547.
export const codePointLength = (str) =>
  typeof str === 'string' ? [...str].length : 0;

const normalize = (value) =>
  typeof value === 'string' && value.length > 0
    ? value.normalize('NFC').toLowerCase()
    : '';

const emailLocalPart = (email) => {
  const normalized = normalize(email);
  if (!normalized) return '';
  const at = normalized.indexOf('@');
  return at === -1 ? normalized : normalized.slice(0, at);
};

// --- Legacy v1 validator (used when nistPasswordV2 flag is OFF) ---
// Remove once the v2 rollout reaches 100% and the flag is retired.
export const checkPassword = (password) => {
  if (!password || password.length === 0) {
    return [false, false, false, false];
  }
  return [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /[0-9]/.test(password),
  ];
};

export const rules = [
  'At least 8 characters',
  'Uppercase letter',
  'Lowercase letter',
  'Number',
];

export const validatePassword = (password, userContext = {}) => {
  const errors = [];
  const value = typeof password === 'string' ? password : '';

  if (codePointLength(value) < MIN_LENGTH) {
    errors.push({
      code: PASSWORD_ERROR_CODES.MIN_LENGTH,
      params: { min: MIN_LENGTH },
    });
  }

  const haystack = normalize(value);
  if (haystack.length > 0) {
    const { email, fname, lname } = userContext;
    const localPart = emailLocalPart(email);
    if (
      localPart.length >= CONTEXT_TERM_MIN_LENGTH &&
      haystack.includes(localPart)
    ) {
      errors.push({ code: PASSWORD_ERROR_CODES.CONTAINS_EMAIL });
    }

    const nameTerms = [fname, lname]
      .map(normalize)
      .filter((term) => term.length >= CONTEXT_TERM_MIN_LENGTH);

    if (nameTerms.some((term) => haystack.includes(term))) {
      errors.push({ code: PASSWORD_ERROR_CODES.CONTAINS_NAME });
    }
  }

  return { isValid: errors.length === 0, errors };
};
